/**
 * LegalTemplates PDF Editor — page-operation and export dialogs
 *
 * Split from the former public/js/pdf-editor.js IIFE (Phase 1, Sep 2026).
 * Page config is injected by the Blade view as window.pdfEditorConfig; pdf.js
 * and pdf-lib are bundled from npm through context.js (no CDN).
 */
import { captureEditSnapshot, pushDocumentHistory, pushHistory } from './annotations.js';
import { pdfjsLib } from './context.js';
import { ensurePdfLib, libs } from './context.js';
import { escapeSvg, redrawAllPages, updateUndoRedoButtons } from './annotations.js';
import {
    compressCloseButton,
    compressCompressedSizeEl,
    compressDownloadButton,
    compressMetaEl,
    compressModalEl,
    compressOriginalSizeEl,
    compressSourceNameEl,
    compressSourceSizeEl,
    compressSubmitButton,
    compressUrl,
    convertCloseButton,
    convertFilenameEl,
    convertModalEl,
    convertOptionButtons,
    convertSubmitButton,
    convertUrl,
    deletePagesActionButton,
    deletePagesCloseButton,
    deletePagesModalEl,
    deletePagesPreviewGridEl,
    deletePagesPreviewMetaEl,
    deletePagesRangesInputEl,
    deletePagesSubmitButton,
    deletePagesSummaryEl,
    extractOptionInputs,
    extractRangesInputEl,
    extractRangesWrapEl,
    mergeButton,
    mergeCloseButton,
    mergeCurrentPagesEl,
    mergeDropEl,
    mergeFileInputEl,
    mergeInsertAfterEl,
    mergeModalEl,
    mergeSelectedEl,
    mergeSelectedNameEl,
    mergeSelectedSizeEl,
    mergeSubmitButton,
    mergeUrl,
    pagesEl,
    protectActionButton,
    protectCloseButton,
    protectConfirmEl,
    protectEnabledEl,
    protectEnabledStateEl,
    protectFeedbackEl,
    protectFieldsEl,
    protectModalEl,
    protectPasswordEl,
    protectRulesEl,
    protectSubmitButton,
    protectToggleConfirmButton,
    protectTogglePasswordButton,
    publicAuthLoginLink,
    publicAuthModalEl,
    publicAuthRegisterLink,
    publicAuthSubtitleEl,
    publicAuthTitleEl,
    publicEditorIsAuthenticated,
    publicEditorMode,
    publicEditorSignatureLoginUrl,
    publicEditorSignatureRegisterUrl,
    publicEditorSignatureUpgradeUrl,
    saveUrl,
    sendDocNameEl,
    sendMessageEl,
    sendModalEl,
    sendRecipientEmailEl,
    sendRecipientNameEl,
    sendSenderEmail,
    sendSenderEmailEl,
    sendSenderFirstEl,
    sendSenderFirstName,
    sendSenderLastEl,
    sendSenderLastName,
    sendUrl,
    session,
    splitExtractActionButton,
    splitExtractCloseButton,
    splitExtractModalEl,
    splitExtractSubmitButton,
    splitExtractUrl,
    splitPanelExtractEl,
    splitPanelSplitEl,
    splitPreviewGridEl,
    splitPreviewMetaEl,
    splitRangesInputEl,
    splitSummaryEl,
    splitTabButtons,
    state,
    watermarkColorEl,
    watermarkFeedbackEl,
    watermarkFontEl,
    watermarkImageNameEl,
    watermarkImagePickButton,
    watermarkImageScaleEl,
    watermarkImageScaleValueEl,
    watermarkModalEl,
    watermarkOpacityEl,
    watermarkOpacityValueEl,
    watermarkPanelImageEl,
    watermarkPanelTextEl,
    watermarkRotationEl,
    watermarkRotationValueEl,
    watermarkSizeEl,
    watermarkSizeValueEl,
    watermarkTabButtons,
    watermarkTextEl,
} from './context.js';
import { clamp, colorsAreClose, formatFileSize, getBaseFileName, hasPendingEdits } from './core.js';
import { publicDocUrl } from './core.js';
import { resetWatermarkDraft } from './drafts.js';
import { hasPendingFormFieldChanges } from './form-fill.js';
import {
    buildEditedPdfBytes,
    downloadBlob,
    downloadBlobObject,
    downloadDataUrl,
    ensureBasePdfBytesValid,
    fetchPdfBytes,
    getApiErrorMessage,
    getCsrfToken,
    hasLoadedDocumentForSend,
    parseDownloadFileName,
    sanitizeDownloadFileName,
    saveEdits,
    setSendBusy,
} from './io.js';
import {
    commitAllInlineEditors,
    fontFamilyToCss,
    isDottedLeaderFragment,
    textFragmentsShareLine,
} from './text.js';
import { setStatus, showAlert } from './ui.js';
import {
    ensureAllPagesPrepared,
    getSplitExtractPreviewThumbForPage,
    renderAllPages,
    setActiveThumb,
} from './viewport.js';

export function openPublicAuthModal(context = 'signature') {
    if (!publicAuthModalEl) {
        const fallbackUrl = publicEditorSignatureLoginUrl || publicEditorSignatureRegisterUrl || publicEditorSignatureUpgradeUrl;
        if (fallbackUrl) {
            window.location.href = fallbackUrl;
        }
        return;
    }

    if (publicAuthLoginLink && publicEditorSignatureLoginUrl) {
        publicAuthLoginLink.setAttribute('href', publicEditorSignatureLoginUrl);
    }

    if (publicAuthRegisterLink && publicEditorSignatureRegisterUrl) {
        publicAuthRegisterLink.setAttribute('href', publicEditorSignatureRegisterUrl);
    }

    if (publicAuthTitleEl) {
        publicAuthTitleEl.textContent = context === 'send'
            ? 'Sign in to send by email'
            : 'Sign in to use Signature';
    }

    if (publicAuthSubtitleEl) {
        publicAuthSubtitleEl.textContent = context === 'send'
            ? 'Create an account or log in to email documents from this editor.'
            : 'Create an account or log in to continue to e-signature tools.';
    }

    publicAuthModalEl.classList.add('active');
    publicAuthModalEl.setAttribute('aria-hidden', 'false');
}

export function closePublicAuthModal() {
    if (!publicAuthModalEl) {
        return;
    }

    publicAuthModalEl.classList.remove('active');
    publicAuthModalEl.setAttribute('aria-hidden', 'true');
}

export function hasPendingProtectionChanges() {
    return state.protect.enabled !== state.protect.savedEnabled || !!state.protect.pendingPassword;
}

export function hasPendingWatermarkChanges() {
    return !!state.watermark.enabled;
}

export function finalizeWatermarkStateAfterSave({
    hasWatermarkChanges = false,
    hasVisualChanges = false,
} = {}) {
    if (hasWatermarkChanges) {
        state.watermark.enabled = false;
        state.watermark.pendingRemoval = false;
        state.watermark.appliedBaseBytes = null;
        setWatermarkFeedback('Watermark applied and saved. This action cannot be undone.');
        syncWatermarkInputUi();
        return;
    }

    if (hasVisualChanges) {
        state.watermark.appliedBaseBytes = null;
    }
}

export function closeSendModal() {
    if (!sendModalEl) {
        return;
    }

    sendModalEl.classList.remove('active');
    sendModalEl.setAttribute('aria-hidden', 'true');
}

export function openSendModal() {
    if (!sendModalEl) {
        return;
    }

    if (!hasLoadedDocumentForSend()) {
        showAlert('Upload a PDF before sending by email.', true);
        setStatus('Upload required');
        return;
    }

    if (publicEditorMode && !publicEditorIsAuthenticated) {
        showAlert('Log in or register to send documents by email.', true);
        setStatus('Login required to send');
        openPublicAuthModal('send');
        return;
    }

    if (sendDocNameEl) {
        sendDocNameEl.value = getBaseFileName();
    }
    if (sendSenderEmailEl) {
        sendSenderEmailEl.value = sendSenderEmail || '';
    }
    if (sendSenderFirstEl && !sendSenderFirstEl.value.trim() && sendSenderFirstName) {
        sendSenderFirstEl.value = sendSenderFirstName;
    }
    if (sendSenderLastEl && !sendSenderLastEl.value.trim() && sendSenderLastName) {
        sendSenderLastEl.value = sendSenderLastName;
    }

    sendModalEl.classList.add('active');
    sendModalEl.setAttribute('aria-hidden', 'false');

    window.setTimeout(() => {
        if (sendRecipientEmailEl) {
            sendRecipientEmailEl.focus();
        }
    }, 10);
}

export async function runSendDocument() {
    if (publicEditorMode && !publicEditorIsAuthenticated) {
        showAlert('Log in or register to send documents by email.', true);
        setStatus('Login required to send');
        openPublicAuthModal('send');
        return;
    }

    if (!hasLoadedDocumentForSend()) {
        showAlert('Upload a PDF before sending by email.', true);
        setStatus('Upload required');
        closeSendModal();
        return;
    }

    if (!sendUrl) {
        showAlert('Send endpoint is unavailable for this document.', true);
        setStatus('Send unavailable');
        return;
    }

    const senderFirstName = String(sendSenderFirstEl ? sendSenderFirstEl.value : '').trim();
    const senderLastName = String(sendSenderLastEl ? sendSenderLastEl.value : '').trim();
    const recipientEmail = String(sendRecipientEmailEl ? sendRecipientEmailEl.value : '').trim();
    const recipientName = String(sendRecipientNameEl ? sendRecipientNameEl.value : '').trim();
    const senderMessage = String(sendMessageEl ? sendMessageEl.value : '').trim();
    const filenameBase = sanitizeDownloadFileName(
        String(sendDocNameEl ? sendDocNameEl.value : '').trim() || getBaseFileName()
    );

    if (!recipientEmail) {
        showAlert('Recipient email is required.', true);
        setStatus('Recipient email required');
        if (sendRecipientEmailEl) {
            sendRecipientEmailEl.focus();
        }
        return;
    }

    if (!senderFirstName || !senderLastName) {
        showAlert('Sender first and last name are required.', true);
        setStatus('Sender name required');
        if (sendSenderFirstEl && !senderFirstName) {
            sendSenderFirstEl.focus();
        } else if (sendSenderLastEl && !senderLastName) {
            sendSenderLastEl.focus();
        }
        return;
    }

    setSendBusy(true);
    setStatus('Preparing email...');

    try {
        const workingBytes = await getWorkingPdfBytesForConvert();
        const pdfBlob = new Blob([workingBytes], { type: 'application/pdf' });
        const formData = new FormData();
        formData.append('_token', getCsrfToken());
        formData.append('file', pdfBlob, `${filenameBase}.pdf`);
        formData.append('doc_name', filenameBase);
        formData.append('recipient_email', recipientEmail);
        formData.append('sender_first_name', senderFirstName);
        formData.append('sender_last_name', senderLastName);
        if (recipientName) {
            formData.append('recipient_name', recipientName);
        }
        if (senderMessage) {
            formData.append('message', senderMessage);
        }

        const response = await fetch(publicDocUrl(sendUrl), {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Accept': 'application/json',
            },
            body: formData,
        });

        const contentType = String(response.headers.get('Content-Type') || '').toLowerCase();
        const payload = contentType.includes('application/json')
            ? await response.json().catch(() => ({}))
            : {};

        if (!response.ok) {
            throw new Error(getApiErrorMessage(payload, 'Could not send this document.'));
        }

        if (!contentType.includes('application/json')) {
            throw new Error('Unexpected response while sending email. Please log in again and retry.');
        }

        closeSendModal();
        showAlert(payload.message || 'Document sent successfully.');
        setStatus('Document sent');
    } catch (error) {
        showAlert(error.message || 'Could not send this document right now.', true);
        setStatus('Send failed');
    } finally {
        setSendBusy(false);
    }
}

export function syncConvertOptionUI() {
    convertOptionButtons.forEach((button) => {
        button.classList.toggle('active', button.dataset.convertFormat === state.convert.format);
    });
}

export function setConvertFormat(format) {
    state.convert.format = format;
    syncConvertOptionUI();
}

export function openConvertModal() {
    if (!convertModalEl) {
        return;
    }

    if (state.activeSuiteTool !== 'convert') {
        showAlert('Select Convert in the sidebar first.');
        return;
    }

    commitAllInlineEditors();
    if (convertFilenameEl && !convertFilenameEl.value.trim()) {
        convertFilenameEl.value = getBaseFileName();
    }

    convertModalEl.classList.add('active');
    convertModalEl.setAttribute('aria-hidden', 'false');
    syncConvertOptionUI();

    if (convertFilenameEl) {
        window.setTimeout(() => {
            convertFilenameEl.focus();
            convertFilenameEl.select();
        }, 10);
    }
}

export function closeConvertModal() {
    if (!convertModalEl) {
        return;
    }

    convertModalEl.classList.remove('active');
    convertModalEl.setAttribute('aria-hidden', 'true');
}

export function setConvertBusy(busy) {
    state.convert.busy = busy;
    if (convertSubmitButton) {
        convertSubmitButton.disabled = busy;
    }
    if (convertCloseButton) {
        convertCloseButton.disabled = busy;
    }
    convertOptionButtons.forEach((button) => {
        button.disabled = busy;
    });
}

export async function openCompressModal() {
    if (!compressModalEl) {
        return;
    }

    if (state.activeSuiteTool !== 'compress') {
        showAlert('Select Compress in the sidebar first.');
        return;
    }

    commitAllInlineEditors();
    resetCompressResult();

    const baseName = sanitizeDownloadFileName(getBaseFileName());
    if (compressSourceNameEl) {
        compressSourceNameEl.textContent = `${baseName}.pdf`;
    }
    if (compressSourceSizeEl) {
        compressSourceSizeEl.textContent = 'Calculating...';
    }
    if (compressOriginalSizeEl) {
        compressOriginalSizeEl.textContent = 'Calculating...';
    }
    if (compressCompressedSizeEl) {
        compressCompressedSizeEl.textContent = '--';
    }
    if (compressMetaEl) {
        compressMetaEl.textContent = 'Compression keeps document quality while reducing download size.';
    }

    compressModalEl.classList.add('active');
    compressModalEl.setAttribute('aria-hidden', 'false');
    setStatus('Preparing compression...');

    try {
        const workingBytes = await getWorkingPdfBytesForConvert();
        const bytes = workingBytes.byteLength || workingBytes.length || 0;
        state.compress.originalBytes = bytes;
        if (compressSourceSizeEl) {
            compressSourceSizeEl.textContent = formatFileSize(bytes);
        }
        if (compressOriginalSizeEl) {
            compressOriginalSizeEl.textContent = formatFileSize(bytes);
        }
        setStatus('Compression ready');
    } catch (error) {
        if (compressSourceSizeEl) {
            compressSourceSizeEl.textContent = '--';
        }
        if (compressOriginalSizeEl) {
            compressOriginalSizeEl.textContent = '--';
        }
        if (compressCompressedSizeEl) {
            compressCompressedSizeEl.textContent = '--';
        }
        showAlert(error.message || 'Could not prepare file for compression.', true);
        setStatus('Compression unavailable');
    }
}

export function closeCompressModal() {
    if (!compressModalEl) {
        return;
    }

    resetCompressResult();
    compressModalEl.classList.remove('active');
    compressModalEl.setAttribute('aria-hidden', 'true');
}

export function resetCompressResult() {
    if (state.compress.resultUrl) {
        URL.revokeObjectURL(state.compress.resultUrl);
    }

    state.compress.compressedBytes = 0;
    state.compress.resultUrl = null;
    state.compress.resultName = '';

    if (compressCompressedSizeEl) {
        compressCompressedSizeEl.textContent = '--';
    }

    if (compressDownloadButton) {
        compressDownloadButton.disabled = true;
    }
}

export function setCompressBusy(busy) {
    state.compress.busy = busy;
    if (compressSubmitButton) {
        compressSubmitButton.disabled = busy;
    }
    if (compressCloseButton) {
        compressCloseButton.disabled = busy;
    }
    if (compressDownloadButton) {
        compressDownloadButton.disabled = busy || !state.compress.resultUrl;
    }
}

export function updateMergeInsertOptions(preferredInsertAfter = null) {
    if (!mergeInsertAfterEl) {
        return;
    }

    const pageCount = state.pdfJsDoc ? Number(state.pdfJsDoc.numPages || 0) : 0;
    mergeInsertAfterEl.innerHTML = '';

    if (mergeCurrentPagesEl) {
        mergeCurrentPagesEl.textContent = pageCount > 0
            ? `Current document pages: ${pageCount}`
            : 'Current document pages: --';
    }

    if (pageCount < 1) {
        const option = document.createElement('option');
        option.value = '0';
        option.textContent = 'Before page 1';
        mergeInsertAfterEl.appendChild(option);
        state.merge.insertAfter = 0;
        return;
    }

    const startOption = document.createElement('option');
    startOption.value = '0';
    startOption.textContent = 'Before page 1';
    mergeInsertAfterEl.appendChild(startOption);

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
        const option = document.createElement('option');
        option.value = String(pageNumber);
        option.textContent = `After page ${pageNumber}`;
        mergeInsertAfterEl.appendChild(option);
    }

    let selected = preferredInsertAfter;
    if (!Number.isFinite(Number(selected))) {
        selected = Number.isFinite(Number(state.merge.insertAfter))
            ? state.merge.insertAfter
            : pageCount;
    }

    selected = Math.max(0, Math.min(Number(selected), pageCount));
    mergeInsertAfterEl.value = String(selected);
    state.merge.insertAfter = selected;
}

export function resetMergeSelection() {
    state.merge.file = null;
    if (mergeFileInputEl) {
        mergeFileInputEl.value = '';
    }
    if (mergeSelectedEl) {
        mergeSelectedEl.classList.add('hidden');
    }
    if (mergeSelectedNameEl) {
        mergeSelectedNameEl.textContent = 'No file selected';
    }
    if (mergeSelectedSizeEl) {
        mergeSelectedSizeEl.textContent = '0.00 KB';
    }
}

export function setMergeSelectedFile(file) {
    state.merge.file = file || null;

    if (!file) {
        resetMergeSelection();
        return;
    }

    if (mergeSelectedEl) {
        mergeSelectedEl.classList.remove('hidden');
    }
    if (mergeSelectedNameEl) {
        mergeSelectedNameEl.textContent = file.name || 'merge.pdf';
    }
    if (mergeSelectedSizeEl) {
        mergeSelectedSizeEl.textContent = formatFileSize(Number(file.size || 0));
    }
}

export function openMergeModal() {
    if (!mergeModalEl) {
        return;
    }

    if (state.activeSuiteTool !== 'merge') {
        showAlert('Select Merge in the sidebar first.');
        return;
    }

    commitAllInlineEditors();
    updateMergeInsertOptions();

    mergeModalEl.classList.add('active');
    mergeModalEl.setAttribute('aria-hidden', 'false');
}

export function closeMergeModal() {
    if (!mergeModalEl) {
        return;
    }

    mergeModalEl.classList.remove('active');
    mergeModalEl.setAttribute('aria-hidden', 'true');
}

export function setMergeBusy(busy) {
    state.merge.busy = busy;
    if (mergeSubmitButton) {
        mergeSubmitButton.disabled = busy;
    }
    if (mergeCloseButton) {
        mergeCloseButton.disabled = busy;
    }
    if (mergeDropEl) {
        mergeDropEl.disabled = busy;
    }
    if (mergeInsertAfterEl) {
        mergeInsertAfterEl.disabled = busy;
    }
    if (mergeButton) {
        mergeButton.disabled = busy;
    }
}

export async function runMergeAndReload() {
    if (!mergeUrl) {
        showAlert('Merge endpoint is unavailable.', true);
        return;
    }

    if (!state.merge.file) {
        showAlert('Select a PDF to merge first.', true);
        return;
    }

    setMergeBusy(true);
    setStatus('Merging PDF...');

    try {
        const workingBytes = await getWorkingPdfBytesForConvert();
        // For undo: the document as it was before the merge.
        const beforeBytes = state.basePdfBytes ? new Uint8Array(state.basePdfBytes) : null;
        const beforeSnapshot = captureEditSnapshot();
        const currentName = sanitizeDownloadFileName(getBaseFileName());
        const insertAfter = Number(mergeInsertAfterEl ? mergeInsertAfterEl.value : state.merge.insertAfter) || 0;

        const currentBlob = new Blob([workingBytes], { type: 'application/pdf' });
        const formData = new FormData();
        formData.append('_token', getCsrfToken());
        formData.append('insert_after_page', String(insertAfter));
        formData.append('file', currentBlob, `${currentName}.pdf`);
        formData.append('merge_file', state.merge.file, state.merge.file.name || 'merge.pdf');

        const response = await fetch(publicDocUrl(mergeUrl), {
            method: 'POST',
            credentials: 'same-origin',
            body: formData,
        });

        if (!response.ok) {
            let message = 'Merge failed. Please try again.';
            const errorText = (await response.text()).trim();
            if (errorText) {
                try {
                    const payload = JSON.parse(errorText);
                    if (payload && payload.message) {
                        message = payload.message;
                    } else {
                        message = errorText;
                    }
                } catch (error) {
                    message = errorText;
                }
            }
            throw new Error(message);
        }

        const payload = await response.json().catch(() => ({}));
        const mergedSourceUrl = String(payload.source_url || session.sourceUrl || '');
        if (!mergedSourceUrl) {
            throw new Error('Merged PDF source URL was not returned.');
        }

        state.basePdfBytes = await fetchPdfBytes(mergedSourceUrl);
        state.annotations = [];
        state.annotationDirty = false;
        pushDocumentHistory({
            label: 'merge',
            beforeBytes,
            afterBytes: new Uint8Array(state.basePdfBytes),
            beforeSnapshot,
            afterSnapshot: captureEditSnapshot(),
        });
        state.selectedAnnotationId = null;
        state.interaction = null;
        state.draft = null;
        updateUndoRedoButtons();

        const focusPage = Math.max(1, Number(payload.insert_after_page || insertAfter) + 1);
        state.lastActivePage = focusPage;

        await renderAllPages();
        updateMergeInsertOptions(payload.insert_after_page ?? insertAfter);

        const targetPage = Math.max(
            1,
            Math.min(
                focusPage,
                state.pdfJsDoc ? Number(state.pdfJsDoc.numPages || focusPage) : focusPage
            )
        );
        const targetShell = pagesEl.querySelector(`[data-page="${targetPage}"]`);
        if (targetShell) {
            targetShell.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setActiveThumb(targetPage);
        }

        resetMergeSelection();
        closeMergeModal();
        showAlert(payload.message || 'PDF merged successfully.');
        setStatus('Merge complete');
    } catch (error) {
        showAlert(error.message || 'Merge failed. Please try again.', true);
        setStatus('Merge failed');
    } finally {
        setMergeBusy(false);
    }
}

export function getCurrentPageCountForSplitExtract() {
    return state.pdfJsDoc ? Number(state.pdfJsDoc.numPages || 0) : 0;
}

export function parseRangeGroupsForSplitExtract(value, pageCount) {
    const tokens = String(value || '')
        .split(',')
        .map((token) => token.trim())
        .filter((token) => token.length > 0);

    if (!tokens.length) {
        throw new Error('Enter at least one page range, like "1,2-3".');
    }

    const groups = [];
    for (const token of tokens) {
        const match = token.match(/^(\d+)(?:\s*-\s*(\d+))?$/);
        if (!match) {
            throw new Error('Invalid range format. Use values like "1,2-3,5".');
        }

        const start = Number(match[1]);
        const end = match[2] ? Number(match[2]) : start;

        if (!Number.isInteger(start) || !Number.isInteger(end)) {
            throw new Error('Ranges must use whole page numbers.');
        }

        if (start < 1 || end < 1 || start > pageCount || end > pageCount) {
            throw new Error(`Page ranges must be between 1 and ${pageCount}.`);
        }

        if (start > end) {
            throw new Error('Each range must go from a lower page to a higher page.');
        }

        groups.push({
            start,
            end,
            label: start === end ? String(start) : `${start}-${end}`,
        });
    }

    return groups;
}

export function expandRangeGroupsToPagesForSplitExtract(groups) {
    const unique = new Set();
    groups.forEach((group) => {
        for (let page = group.start; page <= group.end; page += 1) {
            unique.add(page);
        }
    });

    return Array.from(unique).sort((a, b) => a - b);
}

export function buildSplitExtractPreviewBadges(start, end) {
    const total = Math.max(0, end - start + 1);
    if (total <= 0) {
        return [];
    }

    if (total <= 6) {
        const badges = [];
        for (let page = start; page <= end; page += 1) {
            badges.push({ text: String(page), ellipsis: false });
        }
        return badges;
    }

    return [
        { text: String(start), ellipsis: false },
        { text: String(start + 1), ellipsis: false },
        { text: '...', ellipsis: true },
        { text: String(end - 1), ellipsis: false },
        { text: String(end), ellipsis: false },
    ];
}

export function buildSplitExtractPlan(pageCount = getCurrentPageCountForSplitExtract()) {
    if (pageCount < 1) {
        throw new Error('PDF pages are still loading. Please try again.');
    }

    const mode = state.splitExtract.mode === 'extract' ? 'extract' : 'split';
    const plan = {
        mode,
        extractOption: 'all',
        ranges: '',
        entries: [],
        outputCount: 0,
    };

    if (mode === 'split') {
        const ranges = splitRangesInputEl ? splitRangesInputEl.value.trim() : '';
        const groups = parseRangeGroupsForSplitExtract(ranges, pageCount);
        plan.ranges = ranges;
        plan.entries = groups.map((group, index) => ({
            fileNumber: index + 1,
            rangeLabel: group.label,
            start: group.start,
            end: group.end,
        }));
        plan.outputCount = plan.entries.length;
        return plan;
    }

    const extractOption = state.splitExtract.extractOption === 'selected' ? 'selected' : 'all';
    plan.extractOption = extractOption;

    if (extractOption === 'all') {
        for (let page = 1; page <= pageCount; page += 1) {
            plan.entries.push({
                fileNumber: page,
                rangeLabel: String(page),
                start: page,
                end: page,
            });
        }
        plan.outputCount = plan.entries.length;
        return plan;
    }

    const ranges = extractRangesInputEl ? extractRangesInputEl.value.trim() : '';
    const groups = parseRangeGroupsForSplitExtract(ranges, pageCount);
    const pages = expandRangeGroupsToPagesForSplitExtract(groups);
    plan.ranges = ranges;
    plan.entries = pages.map((page, index) => ({
        fileNumber: index + 1,
        rangeLabel: String(page),
        start: page,
        end: page,
    }));
    plan.outputCount = plan.entries.length;

    return plan;
}

export function renderSplitExtractPreview(plan, errorMessage = '') {
    if (!splitPreviewGridEl || !splitPreviewMetaEl) {
        return;
    }

    if (errorMessage) {
        splitPreviewMetaEl.textContent = 'Invalid input';
        splitPreviewGridEl.innerHTML = `<div class="pdf-split-preview-empty">${escapeSvg(errorMessage)}</div>`;
        return;
    }

    const entries = Array.isArray(plan && plan.entries) ? plan.entries : [];
    const totalCount = Number(plan && plan.outputCount ? plan.outputCount : entries.length);
    if (totalCount < 1 || !entries.length) {
        splitPreviewMetaEl.textContent = 'No files';
        splitPreviewGridEl.innerHTML = '<div class="pdf-split-preview-empty">No output files for current settings.</div>';
        return;
    }

    const maxPreviewCards = 24;
    const visibleEntries = entries.slice(0, maxPreviewCards);
    splitPreviewMetaEl.textContent = totalCount > maxPreviewCards
        ? `Showing ${maxPreviewCards} of ${totalCount} files`
        : `${totalCount} file${totalCount === 1 ? '' : 's'}`;

    splitPreviewGridEl.innerHTML = visibleEntries.map((entry) => {
        const badges = buildSplitExtractPreviewBadges(Number(entry.start), Number(entry.end));
        const badgesHtml = badges.map((badge) => (
            `<span class="pdf-split-preview-badge${badge.ellipsis ? ' ellipsis' : ''}">${escapeSvg(badge.text)}</span>`
        )).join('');
        const previewPage = Math.max(1, Number(entry.start || 1));
        const previewPageCount = Math.max(1, Number(entry.end || previewPage) - previewPage + 1);
        const thumbSrc = getSplitExtractPreviewThumbForPage(previewPage);
        const thumbHtml = thumbSrc
            ? `<div class="pdf-split-preview-thumb"><img src="${thumbSrc}" alt="Preview page ${previewPage}">${previewPageCount > 1 ? `<span class="count">+${previewPageCount - 1}</span>` : ''}</div>`
            : `<div class="pdf-split-preview-thumb fallback">Page ${previewPage}</div>`;

        return `
            <article class="pdf-split-preview-card">
                ${thumbHtml}
                <p class="file">File ${String(entry.fileNumber).padStart(2, '0')}</p>
                <p class="range">Pages ${escapeSvg(entry.rangeLabel)}</p>
                <div class="pdf-split-preview-badges">${badgesHtml}</div>
            </article>
        `;
    }).join('');

    if (totalCount > maxPreviewCards) {
        splitPreviewGridEl.insertAdjacentHTML(
            'beforeend',
            `<div class="pdf-split-preview-empty">+${totalCount - maxPreviewCards} more files will be included in ZIP.</div>`
        );
    }
}

export function setSplitExtractSummary(message, isError = false) {
    if (!splitSummaryEl) {
        return;
    }

    splitSummaryEl.textContent = message;
    splitSummaryEl.classList.toggle('error', !!isError);
}

export function setSplitExtractBusy(busy) {
    state.splitExtract.busy = busy;

    if (splitExtractSubmitButton) {
        splitExtractSubmitButton.disabled = busy;
    }
    if (splitExtractCloseButton) {
        splitExtractCloseButton.disabled = busy;
    }
    if (splitExtractActionButton) {
        splitExtractActionButton.disabled = busy;
    }
}

export function setExtractOption(option) {
    const normalized = option === 'selected' ? 'selected' : 'all';
    state.splitExtract.extractOption = normalized;

    extractOptionInputs.forEach((input) => {
        input.checked = input.value === normalized;
    });

    if (extractRangesWrapEl) {
        extractRangesWrapEl.classList.toggle('hidden', normalized !== 'selected');
    }
}

export function setSplitExtractMode(mode) {
    const normalized = mode === 'extract' ? 'extract' : 'split';
    state.splitExtract.mode = normalized;

    splitTabButtons.forEach((button) => {
        const isActive = button.dataset.splitTab === normalized;
        button.classList.toggle('active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    if (splitPanelSplitEl) {
        splitPanelSplitEl.classList.toggle('hidden', normalized !== 'split');
    }
    if (splitPanelExtractEl) {
        splitPanelExtractEl.classList.toggle('hidden', normalized !== 'extract');
    }
    if (splitExtractSubmitButton) {
        splitExtractSubmitButton.textContent = normalized === 'extract'
            ? 'Extract & Download ZIP'
            : 'Split & Download ZIP';
    }

    syncSplitExtractSummary();
}

export function syncSplitExtractSummary() {
    const pageCount = getCurrentPageCountForSplitExtract();
    if (pageCount < 1) {
        setSplitExtractSummary('PDF pages are still loading. Try again in a moment.', true);
        renderSplitExtractPreview(null, 'PDF pages are still loading. Try again in a moment.');
        return;
    }

    try {
        const plan = buildSplitExtractPlan(pageCount);
        const count = plan.outputCount;

        if (plan.mode === 'split') {
            setSplitExtractSummary(`This split will create ${count} separate PDF file${count === 1 ? '' : 's'}.`);
        } else if (plan.extractOption === 'all') {
            setSplitExtractSummary(`This extract will create ${count} separate PDF file${count === 1 ? '' : 's'} (one per page).`);
        } else {
            setSplitExtractSummary(`This extract will create ${count} separate PDF file${count === 1 ? '' : 's'} from selected pages.`);
        }

        renderSplitExtractPreview(plan);
    } catch (error) {
        const message = error.message || 'Invalid ranges.';
        setSplitExtractSummary(message, true);
        renderSplitExtractPreview(null, message);
    }
}

export function openSplitExtractModal() {
    if (!splitExtractModalEl) {
        return;
    }

    if (state.activeSuiteTool !== 'split-extract') {
        showAlert('Select Split & Extract in the sidebar first.');
        return;
    }

    commitAllInlineEditors();

    if (splitRangesInputEl && !splitRangesInputEl.value.trim()) {
        const pageCount = getCurrentPageCountForSplitExtract();
        if (pageCount >= 3) {
            splitRangesInputEl.value = '1,2-3';
        } else if (pageCount === 2) {
            splitRangesInputEl.value = '1,2';
        } else {
            splitRangesInputEl.value = '1';
        }
    }

    setSplitExtractMode(state.splitExtract.mode || 'split');
    setExtractOption(state.splitExtract.extractOption || 'all');
    splitExtractModalEl.classList.add('active');
    splitExtractModalEl.setAttribute('aria-hidden', 'false');
    syncSplitExtractSummary();
    // Page previews need every thumbnail; refresh once the background
    // preparation has caught up.
    ensureAllPagesPrepared().then(() => {
        if (splitExtractModalEl.classList.contains('active')) {
            syncSplitExtractSummary();
        }
    }).catch(() => {});
}

export function closeSplitExtractModal() {
    if (!splitExtractModalEl) {
        return;
    }

    splitExtractModalEl.classList.remove('active');
    splitExtractModalEl.setAttribute('aria-hidden', 'true');
}

export async function runSplitExtractAndDownload() {
    if (!splitExtractUrl) {
        showAlert('Split/Extract endpoint is unavailable.', true);
        return;
    }

    const pageCount = getCurrentPageCountForSplitExtract();
    if (pageCount < 1) {
        showAlert('PDF pages are still loading. Please try again.', true);
        return;
    }

    setSplitExtractBusy(true);
    setStatus('Preparing split/extract...');

    try {
        const plan = buildSplitExtractPlan(pageCount);
        const mode = plan.mode;
        const filenameBase = sanitizeDownloadFileName(getBaseFileName());
        const formData = new FormData();
        formData.append('_token', getCsrfToken());
        formData.append('mode', mode);
        formData.append('filename', filenameBase);
        const outputCount = plan.outputCount;

        if (mode === 'split') {
            formData.append('ranges', plan.ranges);
        } else {
            formData.append('extract_option', plan.extractOption);
            if (plan.extractOption === 'selected') {
                formData.append('ranges', plan.ranges);
            }
        }

        const workingBytes = await getWorkingPdfBytesForConvert();
        const currentBlob = new Blob([workingBytes], { type: 'application/pdf' });
        formData.append('file', currentBlob, `${filenameBase}.pdf`);

        const response = await fetch(publicDocUrl(splitExtractUrl), {
            method: 'POST',
            credentials: 'same-origin',
            body: formData,
        });

        if (!response.ok) {
            let message = 'Split/Extract failed. Please try again.';
            const errorText = (await response.text()).trim();
            if (errorText) {
                try {
                    const payload = JSON.parse(errorText);
                    if (payload && payload.message) {
                        message = payload.message;
                    } else {
                        message = errorText;
                    }
                } catch (error) {
                    message = errorText;
                }
            }

            throw new Error(message);
        }

        const zipBlob = await response.blob();
        const fallbackName = `${filenameBase}-${mode === 'extract' ? 'extracted-pages' : 'split-files'}.zip`;
        const parsedFilename = parseDownloadFileName(response.headers.get('Content-Disposition'), fallbackName);
        downloadBlobObject(zipBlob, parsedFilename);

        closeSplitExtractModal();
        showAlert(`${outputCount} PDF file${outputCount === 1 ? '' : 's'} prepared and downloaded as ZIP.`);
        setStatus('Split/Extract complete');
    } catch (error) {
        showAlert(error.message || 'Split/Extract failed. Please try again.', true);
        setStatus('Split/Extract failed');
        syncSplitExtractSummary();
    } finally {
        setSplitExtractBusy(false);
    }
}

export function setDeletePagesSummary(message, isError = false) {
    if (!deletePagesSummaryEl) {
        return;
    }

    deletePagesSummaryEl.textContent = message;
    deletePagesSummaryEl.classList.toggle('error', !!isError);
}

export function setDeletePagesBusy(busy) {
    state.deletePages.busy = busy;
    if (deletePagesSubmitButton) {
        deletePagesSubmitButton.disabled = busy;
    }
    if (deletePagesCloseButton) {
        deletePagesCloseButton.disabled = busy;
    }
    if (deletePagesActionButton) {
        deletePagesActionButton.disabled = busy;
    }
    if (deletePagesRangesInputEl) {
        deletePagesRangesInputEl.disabled = busy;
    }
}

export function buildDeletePagesPlan(pageCount) {
    const ranges = deletePagesRangesInputEl ? deletePagesRangesInputEl.value.trim() : '';
    const groups = parseRangeGroupsForSplitExtract(ranges, pageCount);
    const pages = expandRangeGroupsToPagesForSplitExtract(groups);

    if (!pages.length) {
        throw new Error('Select at least one page to delete.');
    }

    if (pages.length >= pageCount) {
        throw new Error('At least one page must remain in the document.');
    }

    return {
        ranges,
        pages,
        pageCount,
        deleteCount: pages.length,
        remainingCount: pageCount - pages.length,
    };
}

export function renderDeletePagesPreview(plan, errorMessage = '') {
    if (!deletePagesPreviewGridEl || !deletePagesPreviewMetaEl || !deletePagesSubmitButton) {
        return;
    }

    if (errorMessage) {
        deletePagesPreviewMetaEl.textContent = 'Invalid selection';
        deletePagesPreviewGridEl.innerHTML = `<div class="pdf-split-preview-empty">${escapeSvg(errorMessage)}</div>`;
        deletePagesSubmitButton.disabled = true;
        return;
    }

    if (!plan || !plan.pages.length) {
        deletePagesPreviewMetaEl.textContent = 'No pages selected';
        deletePagesPreviewGridEl.innerHTML = '<div class="pdf-split-preview-empty">Enter page numbers to preview which pages will be deleted.</div>';
        deletePagesSubmitButton.disabled = true;
        return;
    }

    const pages = plan.pages;
    const maxPreviewCards = 10;
    const visiblePages = pages.slice(0, maxPreviewCards);
    deletePagesPreviewMetaEl.textContent = pages.length > maxPreviewCards
        ? `${pages.length} pages (${maxPreviewCards} shown)`
        : `${pages.length} page${pages.length === 1 ? '' : 's'}`;

    deletePagesPreviewGridEl.innerHTML = visiblePages.map((pageNumber) => {
        const thumbSrc = getSplitExtractPreviewThumbForPage(pageNumber);
        const thumbHtml = thumbSrc
            ? `<div class="pdf-split-preview-thumb"><img src="${thumbSrc}" alt="Page ${pageNumber} preview"></div>`
            : `<div class="pdf-split-preview-thumb fallback">Page ${pageNumber}</div>`;

        return `
            <article class="pdf-split-preview-card">
                ${thumbHtml}
                <p class="file">Page ${escapeSvg(String(pageNumber))}</p>
                <p class="range">Will be removed</p>
            </article>
        `;
    }).join('');

    if (pages.length > maxPreviewCards) {
        deletePagesPreviewGridEl.insertAdjacentHTML(
            'beforeend',
            `<div class="pdf-split-preview-empty">+${pages.length - maxPreviewCards} more page${pages.length - maxPreviewCards === 1 ? '' : 's'} selected.</div>`
        );
    }

    deletePagesSubmitButton.disabled = false;
}

export function syncDeletePagesSummary() {
    const pageCount = getCurrentPageCountForSplitExtract();
    if (pageCount < 1) {
        const message = 'PDF pages are still loading. Try again in a moment.';
        setDeletePagesSummary(message, true);
        renderDeletePagesPreview(null, message);
        return;
    }

    if (pageCount === 1) {
        const message = 'This document has only 1 page. At least one page must remain.';
        setDeletePagesSummary(message, true);
        renderDeletePagesPreview(null, message);
        return;
    }

    try {
        const plan = buildDeletePagesPlan(pageCount);
        setDeletePagesSummary(
            `This will delete ${plan.deleteCount} page${plan.deleteCount === 1 ? '' : 's'}. ${plan.remainingCount} page${plan.remainingCount === 1 ? '' : 's'} will remain.`
        );
        renderDeletePagesPreview(plan);
    } catch (error) {
        const message = error.message || 'Invalid page selection.';
        setDeletePagesSummary(message, true);
        renderDeletePagesPreview(null, message);
    }
}

export function openDeletePagesModal() {
    if (!deletePagesModalEl) {
        return;
    }

    if (state.activeSuiteTool !== 'delete-pages') {
        showAlert('Select Delete in the sidebar first.');
        return;
    }

    commitAllInlineEditors();

    const pageCount = getCurrentPageCountForSplitExtract();
    if (deletePagesRangesInputEl && !deletePagesRangesInputEl.value.trim() && pageCount > 0) {
        deletePagesRangesInputEl.value = String(clamp(state.lastActivePage || 1, 1, Math.max(1, pageCount)));
    }

    deletePagesModalEl.classList.add('active');
    deletePagesModalEl.setAttribute('aria-hidden', 'false');
    syncDeletePagesSummary();
    ensureAllPagesPrepared().then(() => {
        if (deletePagesModalEl.classList.contains('active')) {
            syncDeletePagesSummary();
        }
    }).catch(() => {});

    if (deletePagesRangesInputEl) {
        deletePagesRangesInputEl.focus();
        deletePagesRangesInputEl.select();
    }
}

export function closeDeletePagesModal() {
    if (!deletePagesModalEl) {
        return;
    }

    deletePagesModalEl.classList.remove('active');
    deletePagesModalEl.setAttribute('aria-hidden', 'true');
}

export async function runDeletePagesAndSave() {
    await ensurePdfLib();
    const pageCount = getCurrentPageCountForSplitExtract();
    if (pageCount < 1) {
        showAlert('PDF pages are still loading. Please try again.', true);
        return;
    }

    let plan;
    try {
        plan = buildDeletePagesPlan(pageCount);
    } catch (error) {
        syncDeletePagesSummary();
        showAlert(error.message || 'Invalid page selection.', true);
        return;
    }

    const confirmed = window.confirm(
        `Delete ${plan.deleteCount} page${plan.deleteCount === 1 ? '' : 's'} from this document now?`
    );
    if (!confirmed) {
        return;
    }

    setDeletePagesBusy(true);
    setStatus('Deleting selected pages...');

    try {
        if (hasPendingEdits()) {
            const saved = await saveEdits({
                silentNoEdits: true,
                silentSuccess: true,
            });
            if (!saved) {
                throw new Error('Could not save pending edits before deleting pages.');
            }
        }

        const baseBytes = await ensureBasePdfBytesValid();
        // For undo: the document and edit state as they are right now
        // (pending edits were just saved into the bytes above).
        const beforeBytes = new Uint8Array(baseBytes);
        const beforeSnapshot = captureEditSnapshot();
        const pdfDoc = await libs.PDFLib.PDFDocument.load(baseBytes);
        const totalPages = pdfDoc.getPageCount();
        const deleteIndexes = Array.from(new Set(
            plan.pages
                .filter((page) => page >= 1 && page <= totalPages)
                .map((page) => page - 1)
        )).sort((a, b) => b - a);

        if (!deleteIndexes.length) {
            throw new Error('No valid pages found to delete.');
        }

        if (deleteIndexes.length >= totalPages) {
            throw new Error('At least one page must remain in the document.');
        }

        deleteIndexes.forEach((pageIndex) => {
            pdfDoc.removePage(pageIndex);
        });

        const editedBytes = new Uint8Array(await pdfDoc.save());
        const blob = new Blob([editedBytes], { type: 'application/pdf' });
        const formData = new FormData();
        formData.append('file', blob, `${session.docName || 'document'}.pdf`);

        const response = await fetch(publicDocUrl(saveUrl), {
            method: 'POST',
            headers: {
                'X-CSRF-TOKEN': getCsrfToken(),
                'Accept': 'application/json',
            },
            body: formData,
            credentials: 'same-origin',
        });

        if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(getApiErrorMessage(payload, 'Failed to delete selected pages.'));
        }

        const payload = await response.json().catch(() => ({}));

        state.basePdfBytes = editedBytes;
        state.annotations = [];
        state.annotationDirty = false;
        pushDocumentHistory({
            label: `delete ${deleteIndexes.length} page${deleteIndexes.length === 1 ? '' : 's'}`,
            beforeBytes,
            afterBytes: new Uint8Array(editedBytes),
            beforeSnapshot,
            afterSnapshot: captureEditSnapshot(),
        });
        state.selectedAnnotationId = null;
        state.interaction = null;
        state.draft = null;
        updateUndoRedoButtons();

        if (payload && payload.protection) {
            const savedEnabled = !!payload.protection.enabled;
            const savedHasPassword = !!payload.protection.has_password;
            state.protect.savedEnabled = savedEnabled;
            state.protect.savedHasPassword = savedHasPassword;
            state.protect.enabled = savedEnabled;
            state.protect.hasPassword = savedHasPassword;
            state.protect.pendingPassword = '';
            state.protect.showPassword = false;
            state.protect.showConfirm = false;
            syncProtectInputVisibility();
            syncProtectEnabledUi();
            updateProtectRulesUi();
            setProtectFeedback(getProtectDefaultFeedbackMessage());
        }

        const nextPageCount = totalPages - deleteIndexes.length;
        const targetPage = Math.max(1, Math.min(plan.pages[0], nextPageCount));
        state.lastActivePage = targetPage;
        if (deletePagesRangesInputEl) {
            deletePagesRangesInputEl.value = String(targetPage);
        }

        closeDeletePagesModal();
        await renderAllPages();

        const targetShell = pagesEl.querySelector(`[data-page="${targetPage}"]`);
        if (targetShell) {
            targetShell.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setActiveThumb(targetPage);
        }

        showAlert(`${deleteIndexes.length} page${deleteIndexes.length === 1 ? '' : 's'} deleted and saved.`);
        setStatus('Pages deleted');
    } catch (error) {
        showAlert(error.message || 'Delete failed. Please try again.', true);
        setStatus('Delete failed');
        syncDeletePagesSummary();
    } finally {
        setDeletePagesBusy(false);
    }
}

export function evaluateProtectPasswordRules(password) {
    const value = String(password || '');
    return {
        length: value.length >= 8,
        letterNumber: /[A-Za-z]/.test(value) && /\d/.test(value),
        upperLower: /[A-Z]/.test(value) && /[a-z]/.test(value),
        special: /[^A-Za-z0-9]/.test(value),
    };
}

export function getProtectDefaultFeedbackMessage() {
    if (!state.protect.enabled) {
        return 'Password protection is off. Turn it on to require a password for this document.';
    }

    if (state.protect.pendingPassword) {
        return 'A new password is staged. Click Save Edits to apply it to downloads.';
    }

    if (state.protect.hasPassword) {
        return 'A saved password already protects downloads. Enter a new one only if you want to replace it.';
    }

    return 'Choose a strong password and keep it safe. We cannot recover it later.';
}

export function syncProtectEnabledUi() {
    if (protectEnabledEl) {
        protectEnabledEl.checked = !!state.protect.enabled;
    }

    if (protectEnabledStateEl) {
        const stateLabel = state.protect.enabled ? 'On' : 'Off';
        protectEnabledStateEl.textContent = stateLabel;
        protectEnabledStateEl.classList.toggle('on', state.protect.enabled);
        protectEnabledStateEl.classList.toggle('off', !state.protect.enabled);
    }

    if (protectFieldsEl) {
        protectFieldsEl.classList.toggle('is-off', !state.protect.enabled);
    }

    if (protectSubmitButton) {
        protectSubmitButton.textContent = state.protect.enabled ? 'Apply Protection' : 'Apply Setting';
    }
}

export function updateProtectRulesUi() {
    if (!protectRulesEl) {
        return;
    }

    const rules = evaluateProtectPasswordRules(protectPasswordEl ? protectPasswordEl.value : '');
    const items = Array.from(protectRulesEl.querySelectorAll('.rule[data-rule]'));
    items.forEach((item) => {
        const key = item.dataset.rule || '';
        item.classList.toggle('passed', !!rules[key]);
    });
}

export function setProtectFeedback(message, type = 'neutral') {
    if (!protectFeedbackEl) {
        return;
    }

    protectFeedbackEl.textContent = message;
    protectFeedbackEl.classList.remove('error', 'success');
    if (type === 'error') {
        protectFeedbackEl.classList.add('error');
    } else if (type === 'success') {
        protectFeedbackEl.classList.add('success');
    }
}

export function syncProtectInputVisibility() {
    if (protectPasswordEl) {
        protectPasswordEl.type = state.protect.showPassword ? 'text' : 'password';
    }
    if (protectConfirmEl) {
        protectConfirmEl.type = state.protect.showConfirm ? 'text' : 'password';
    }
    if (protectTogglePasswordButton) {
        protectTogglePasswordButton.textContent = state.protect.showPassword ? 'Hide' : 'Show';
    }
    if (protectToggleConfirmButton) {
        protectToggleConfirmButton.textContent = state.protect.showConfirm ? 'Hide' : 'Show';
    }
}

export function resetProtectForm() {
    if (protectPasswordEl) {
        protectPasswordEl.value = '';
    }
    if (protectConfirmEl) {
        protectConfirmEl.value = '';
    }

    state.protect.showPassword = false;
    state.protect.showConfirm = false;
    syncProtectInputVisibility();
    syncProtectEnabledUi();
    updateProtectRulesUi();
    setProtectFeedback(getProtectDefaultFeedbackMessage());
}

export function validateProtectForm() {
    if (!state.protect.enabled) {
        return {
            ok: true,
            message: '',
            password: '',
        };
    }

    const password = protectPasswordEl ? protectPasswordEl.value : '';
    const confirm = protectConfirmEl ? protectConfirmEl.value : '';
    const hasTypedPassword = password !== '' || confirm !== '';

    if (!hasTypedPassword) {
        if (state.protect.hasPassword) {
            return {
                ok: true,
                message: '',
                password: '',
            };
        }

        return {
            ok: false,
            message: 'Enter a password to enable download protection.',
            password: '',
        };
    }

    const rules = evaluateProtectPasswordRules(password);

    if (!rules.length || !rules.letterNumber || !rules.upperLower || !rules.special) {
        return {
            ok: false,
            message: 'Password does not meet all security requirements.',
            password: '',
        };
    }

    if (password !== confirm) {
        return {
            ok: false,
            message: 'Password and confirmation do not match.',
            password: '',
        };
    }

    return {
        ok: true,
        message: '',
        password,
    };
}

export function setProtectBusy(busy) {
    state.protect.busy = busy;
    if (protectSubmitButton) {
        protectSubmitButton.disabled = busy;
    }
    if (protectCloseButton) {
        protectCloseButton.disabled = busy;
    }
    if (protectEnabledEl) {
        protectEnabledEl.disabled = busy;
    }
    if (protectTogglePasswordButton) {
        protectTogglePasswordButton.disabled = busy || !state.protect.enabled;
    }
    if (protectToggleConfirmButton) {
        protectToggleConfirmButton.disabled = busy || !state.protect.enabled;
    }
    if (protectPasswordEl) {
        protectPasswordEl.disabled = busy || !state.protect.enabled;
    }
    if (protectConfirmEl) {
        protectConfirmEl.disabled = busy || !state.protect.enabled;
    }
    if (protectActionButton) {
        protectActionButton.disabled = busy;
    }
}

export function openProtectModal() {
    if (!protectModalEl) {
        return;
    }

    if (state.activeSuiteTool !== 'protect') {
        showAlert('Select Protect in the sidebar first.');
        return;
    }

    commitAllInlineEditors();
    state.protect.modalSnapshotEnabled = !!state.protect.enabled;
    resetProtectForm();
    protectModalEl.classList.add('active');
    protectModalEl.setAttribute('aria-hidden', 'false');
    setProtectBusy(false);
    if (state.protect.enabled && protectPasswordEl) {
        window.setTimeout(() => {
            protectPasswordEl.focus();
        }, 15);
    } else if (protectEnabledEl) {
        window.setTimeout(() => {
            protectEnabledEl.focus();
        }, 15);
    }
}

export function closeProtectModal(persistChanges = false) {
    if (!protectModalEl) {
        return;
    }

    const wasOpen = protectModalEl.classList.contains('active');
    if (!persistChanges && wasOpen) {
        state.protect.enabled = !!state.protect.modalSnapshotEnabled;
        syncProtectEnabledUi();
        setProtectFeedback(getProtectDefaultFeedbackMessage());
    }

    protectModalEl.classList.remove('active');
    protectModalEl.setAttribute('aria-hidden', 'true');
}

export function applyProtectSettings() {
    const validation = validateProtectForm();
    if (!validation.ok) {
        setProtectFeedback(validation.message, 'error');
        showAlert(validation.message, true);
        return;
    }

    if (!state.protect.enabled) {
        state.protect.pendingPassword = '';
        syncProtectEnabledUi();

        if (!hasPendingProtectionChanges()) {
            setProtectFeedback('Protection is already off for this document.', 'success');
            closeProtectModal(true);
            setStatus('Protection unchanged');
            return;
        }

        setProtectFeedback('Protection will be turned off when you click Save Edits.', 'success');
        closeProtectModal(true);
        showAlert('Protection disabled. Click Save Edits to apply.');
        setStatus('Protection off (unsaved)');
        return;
    }

    if (validation.password) {
        state.protect.pendingPassword = validation.password;
        state.protect.hasPassword = true;
    } else if (!state.protect.hasPassword) {
        setProtectFeedback('Enter a password to enable download protection.', 'error');
        showAlert('Enter a password to enable download protection.', true);
        return;
    }

    syncProtectEnabledUi();

    if (!hasPendingProtectionChanges()) {
        setProtectFeedback('Protection is already enabled for this document.', 'success');
        closeProtectModal(true);
        setStatus('Protection unchanged');
        return;
    }

    const usingExistingPassword = !validation.password && state.protect.savedHasPassword;
    const feedback = usingExistingPassword
        ? 'Protection enabled with your existing saved password. Click Save Edits to apply changes.'
        : 'Protection enabled. Click Save Edits to apply this password.';
    setProtectFeedback(feedback, 'success');
    closeProtectModal(true);
    showAlert('Protection updated. Click Save Edits to apply.');
    setStatus('Protection ready (unsaved)');
}

export function setWatermarkFeedback(message) {
    if (!watermarkFeedbackEl) {
        return;
    }

    watermarkFeedbackEl.textContent = message;
}

export function syncWatermarkInputUi() {
    if (watermarkTextEl) {
        watermarkTextEl.value = state.watermark.text || '';
    }

    if (watermarkFontEl) {
        watermarkFontEl.value = state.watermark.fontFamily || 'Helvetica';
    }

    if (watermarkSizeEl) {
        watermarkSizeEl.value = String(Math.round(Number(state.watermark.fontSize || 56)));
    }

    if (watermarkSizeValueEl) {
        watermarkSizeValueEl.textContent = `${Math.round(Number(state.watermark.fontSize || 56))}px`;
    }

    if (watermarkColorEl) {
        watermarkColorEl.value = String(state.watermark.color || '#64748b');
    }

    if (watermarkOpacityEl) {
        watermarkOpacityEl.value = String(Math.round(clamp(Number(state.watermark.opacity || 0.2) * 100, 5, 90)));
    }

    if (watermarkOpacityValueEl) {
        watermarkOpacityValueEl.textContent = `${Math.round(clamp(Number(state.watermark.opacity || 0.2) * 100, 5, 90))}%`;
    }

    if (watermarkRotationEl) {
        watermarkRotationEl.value = String(Math.round(clamp(Number(state.watermark.rotation || 0), -90, 90)));
    }

    if (watermarkRotationValueEl) {
        watermarkRotationValueEl.textContent = `${Math.round(clamp(Number(state.watermark.rotation || 0), -90, 90))}°`;
    }

    if (watermarkImageScaleEl) {
        watermarkImageScaleEl.value = String(Math.round(clamp(Number(state.watermark.imageScale || 0.45) * 100, 15, 85)));
    }

    if (watermarkImageScaleValueEl) {
        watermarkImageScaleValueEl.textContent = `${Math.round(clamp(Number(state.watermark.imageScale || 0.45) * 100, 15, 85))}%`;
    }

    if (watermarkImageNameEl) {
        watermarkImageNameEl.textContent = state.watermark.imageDataUrl
            ? 'Image selected and ready'
            : 'No image selected';
    }
}

export function syncWatermarkTabUi() {
    const isImage = state.watermark.type === 'image';
    watermarkTabButtons.forEach((button) => {
        const isActive = button.dataset.watermarkTab === state.watermark.type;
        button.classList.toggle('active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    if (watermarkPanelTextEl) {
        watermarkPanelTextEl.classList.toggle('hidden', isImage);
    }

    if (watermarkPanelImageEl) {
        watermarkPanelImageEl.classList.toggle('hidden', !isImage);
    }
}

export function setWatermarkType(type) {
    state.watermark.type = type === 'image' ? 'image' : 'text';
    syncWatermarkTabUi();
    if (state.watermark.enabled) {
        redrawAllPages();
    }
}

export function openWatermarkModal() {
    if (!watermarkModalEl) {
        return;
    }

    if (state.activeSuiteTool !== 'watermark') {
        showAlert('Select Watermark in the sidebar first.');
        return;
    }

    commitAllInlineEditors();
    syncWatermarkInputUi();
    syncWatermarkTabUi();
    if (state.watermark.enabled) {
        setWatermarkFeedback('Watermark is staged. Click Save Edits to apply it to every page.');
    } else {
        setWatermarkFeedback('Saved watermarks cannot be undone. Click Apply, then Save Edits to apply permanently.');
    }

    watermarkModalEl.classList.add('active');
    watermarkModalEl.setAttribute('aria-hidden', 'false');

    window.setTimeout(() => {
        if (state.watermark.type === 'image') {
            if (watermarkImagePickButton) {
                watermarkImagePickButton.focus();
            }
        } else if (watermarkTextEl) {
            watermarkTextEl.focus();
        }
    }, 15);
}

export function closeWatermarkModal() {
    if (!watermarkModalEl) {
        return;
    }

    watermarkModalEl.classList.remove('active');
    watermarkModalEl.setAttribute('aria-hidden', 'true');
}

export function applyWatermarkSettings() {
    const mode = state.watermark.type === 'image' ? 'image' : 'text';

    if (mode === 'text') {
        const text = String(state.watermark.text || '').trim();
        if (!text) {
            showAlert('Enter watermark text first.', true);
            setWatermarkFeedback('Watermark text is required before applying.');
            return;
        }

        state.watermark.text = text;
    } else if (!state.watermark.imageDataUrl) {
        showAlert('Choose a watermark image first.', true);
        setWatermarkFeedback('Select a PNG or JPG image before applying.');
        return;
    }

    pushHistory('watermark');
    state.watermark.pendingRemoval = false;
    state.watermark.enabled = true;
    setWatermarkFeedback('Watermark is ready. Click Save Edits to apply it permanently to every page.');
    closeWatermarkModal();
    redrawAllPages();
    showAlert('Watermark staged. Click Save Edits to apply it permanently to every page.');
    setStatus('Watermark ready (unsaved)');
}

export function removeWatermarkSettings() {
    if (!state.watermark.enabled) {
        setWatermarkFeedback('No staged watermark to remove. Saved watermarks cannot be undone.');
        showAlert('No staged watermark to remove. Saved watermarks cannot be undone.');
        return;
    }

    pushHistory('watermark');
    resetWatermarkDraft();
    closeWatermarkModal();
    showAlert('Staged watermark removed.');
    setStatus('Watermark removed');
}

export async function getWorkingPdfBytesForConvert() {
    commitAllInlineEditors();

    if (state.annotations.length || hasPendingWatermarkChanges() || hasPendingFormFieldChanges()) {
        return buildEditedPdfBytes();
    }

    return ensureBasePdfBytesValid();
}

export async function convertPdfToImagesAndDownload(bytes, format, filenameBase) {
    const ext = format === 'jpg' ? 'jpg' : 'png';
    const mimeType = format === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = format === 'jpg' ? 0.92 : undefined;

    // Copy: pdf.js transfers the buffer to its worker, which would detach
    // state.basePdfBytes when converting the unedited base document.
    const loadingTask = pdfjsLib.getDocument({ data: bytes.slice() });
    const pdfDoc = await loadingTask.promise;
    const pageCount = pdfDoc.numPages;

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
        const page = await pdfDoc.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.floor(viewport.width));
        canvas.height = Math.max(1, Math.floor(viewport.height));

        const context = canvas.getContext('2d', { alpha: false });
        await page.render({
            intent: 'print',
            canvasContext: context,
            viewport,
        }).promise;

        const dataUrl = canvas.toDataURL(mimeType, quality);
        const suffix = pageCount > 1 ? `-page-${pageNumber}` : '';
        downloadDataUrl(dataUrl, `${filenameBase}${suffix}.${ext}`);
        await new Promise((resolve) => setTimeout(resolve, 40));
    }

    setStatus(`${pageCount} ${format.toUpperCase()} file${pageCount === 1 ? '' : 's'} downloaded`);
}

export async function runConvertAndDownload() {
    const format = state.convert.format;
    const filenameBase = sanitizeDownloadFileName(convertFilenameEl ? convertFilenameEl.value : '');
    const extensionByFormat = {
        pdf: 'pdf',
        word: 'docx',
        excel: 'xlsx',
        powerpoint: 'pptx',
        jpg: 'jpg',
        png: 'png',
    };

    if (!format) {
        showAlert('Choose a format to convert to.', true);
        return;
    }

    setConvertBusy(true);
    setStatus('Preparing conversion...');

    try {
        const workingBytes = await getWorkingPdfBytesForConvert();

        if (format === 'pdf') {
            downloadBlob(workingBytes, `${filenameBase}.pdf`);
            setStatus('Converted PDF downloaded');
            closeConvertModal();
            return;
        }

        if (format === 'jpg' || format === 'png') {
            await convertPdfToImagesAndDownload(workingBytes, format, filenameBase);
            closeConvertModal();
            return;
        }

        if (format === 'word' || format === 'excel' || format === 'powerpoint') {
            if (!convertUrl) {
                throw new Error('Conversion endpoint is unavailable.');
            }

            const officeBlob = new Blob([workingBytes], { type: 'application/pdf' });
            const formData = new FormData();
            formData.append('_token', getCsrfToken());
            formData.append('format', format);
            formData.append('filename', filenameBase);
            formData.append('file', officeBlob, `${filenameBase}.pdf`);

            const response = await fetch(publicDocUrl(convertUrl), {
                method: 'POST',
                credentials: 'same-origin',
                body: formData,
            });

            if (!response.ok) {
                let message = 'Conversion failed. Please try again.';
                const errorText = (await response.text()).trim();

                if (errorText) {
                    try {
                        const payload = JSON.parse(errorText);
                        if (payload && payload.message) {
                            message = payload.message;
                        } else {
                            message = errorText;
                        }
                    } catch (error) {
                        message = errorText;
                    }
                }

                throw new Error(message);
            }

            const convertedBlob = await response.blob();
            const fallbackFilename = `${filenameBase}.${extensionByFormat[format] || 'bin'}`;
            const parsedFilename = parseDownloadFileName(response.headers.get('Content-Disposition'), fallbackFilename);
            downloadBlobObject(convertedBlob, parsedFilename);
            setStatus(`${format.charAt(0).toUpperCase() + format.slice(1)} download ready`);
            closeConvertModal();
            return;
        }

        showAlert('Selected format is not supported yet.', true);
        setStatus('Unsupported format');
    } catch (error) {
        showAlert(error.message || 'Conversion failed. Please try again.', true);
        setStatus('Conversion failed');
    } finally {
        setConvertBusy(false);
    }
}

export async function runCompressAndDownload() {
    const filenameBase = sanitizeDownloadFileName(getBaseFileName());
    if (!compressUrl) {
        showAlert('Compression endpoint is unavailable.', true);
        return;
    }

    setCompressBusy(true);
    setStatus('Compressing PDF...');

    try {
        const workingBytes = await getWorkingPdfBytesForConvert();
        const workingSize = workingBytes.byteLength || workingBytes.length || 0;
        state.compress.originalBytes = workingSize;

        const pdfBlob = new Blob([workingBytes], { type: 'application/pdf' });
        const formData = new FormData();
        formData.append('_token', getCsrfToken());
        formData.append('filename', filenameBase);
        formData.append('file', pdfBlob, `${filenameBase}.pdf`);

        const response = await fetch(publicDocUrl(compressUrl), {
            method: 'POST',
            credentials: 'same-origin',
            body: formData,
        });

        if (!response.ok) {
            let message = 'Compression failed. Please try again.';
            const errorText = (await response.text()).trim();
            if (errorText) {
                try {
                    const payload = JSON.parse(errorText);
                    if (payload && payload.message) {
                        message = payload.message;
                    } else {
                        message = errorText;
                    }
                } catch (error) {
                    message = errorText;
                }
            }

            throw new Error(message);
        }

        const compressedBlob = await response.blob();
        const fallbackFilename = `${filenameBase}-compressed.pdf`;
        const parsedFilename = parseDownloadFileName(response.headers.get('Content-Disposition'), fallbackFilename);

        const headerOriginal = Number(response.headers.get('X-Original-Bytes'));
        const headerCompressed = Number(response.headers.get('X-Compressed-Bytes'));
        const originalBytes = Number.isFinite(headerOriginal) && headerOriginal > 0 ? headerOriginal : workingSize;
        const compressedBytes = Number.isFinite(headerCompressed) && headerCompressed > 0 ? headerCompressed : compressedBlob.size;
        const savedBytes = Math.max(0, originalBytes - compressedBytes);

        if (state.compress.resultUrl) {
            URL.revokeObjectURL(state.compress.resultUrl);
        }
        state.compress.resultUrl = URL.createObjectURL(compressedBlob);
        state.compress.resultName = parsedFilename;
        state.compress.compressedBytes = compressedBytes;

        if (compressSourceSizeEl) {
            compressSourceSizeEl.textContent = formatFileSize(originalBytes);
        }
        if (compressOriginalSizeEl) {
            compressOriginalSizeEl.textContent = formatFileSize(originalBytes);
        }
        if (compressCompressedSizeEl) {
            compressCompressedSizeEl.textContent = formatFileSize(compressedBytes);
        }

        if (compressMetaEl) {
            if (savedBytes > 0) {
                compressMetaEl.textContent = `Ready to download. Saved ${formatFileSize(savedBytes)} (${formatFileSize(originalBytes)} -> ${formatFileSize(compressedBytes)}).`;
            } else {
                compressMetaEl.textContent = `This file can't be reduced any smaller. Download size remains ${formatFileSize(compressedBytes)}.`;
            }
        }

        if (savedBytes > 0) {
            showAlert(`Compression ready. ${formatFileSize(originalBytes)} -> ${formatFileSize(compressedBytes)}.`);
        } else {
            showAlert("This file can't be reduced any smaller.");
        }

        if (compressDownloadButton) {
            compressDownloadButton.disabled = false;
        }

        setStatus('Compression ready');
    } catch (error) {
        showAlert(error.message || 'Compression failed. Please try again.', true);
        setStatus('Compression failed');
    } finally {
        setCompressBusy(false);
    }
}

export function textFragmentsCanMerge(previous, next, base) {
    if (!previous || !next || !base || !textFragmentsShareLine(base, next)) {
        return false;
    }

    if (isDottedLeaderFragment(previous) || isDottedLeaderFragment(next)) {
        return false;
    }

    const previousRight = Number(previous.x || 0) + Number(previous.width || 0);
    const gap = Number(next.x || 0) - previousRight;
    const baseFontSize = Math.max(8, Number(base.fontSize || 12));
    // Only fold together fragments that are visually contiguous (kerning/letter-spacing splits of
    // the same token), not ones separated by a real word space. An inter-word space is ~0.25em, so
    // a 0.18em ceiling keeps separate words editable on their own — clicking "Trade" no longer
    // captures the whole line. See bug #4.
    const gapLimit = Math.max(2, baseFontSize * 0.18);

    if (gap < -Math.max(3, baseFontSize * 0.25)) {
        return false;
    }

    if (gap > gapLimit) {
        return false;
    }

    const baseColor = String(base.color || '').toLowerCase();
    const nextColor = String(next.color || '').toLowerCase();
    if (baseColor && nextColor && !colorsAreClose(baseColor, nextColor)) {
        return false;
    }

    const baseBackground = String(base.backgroundColor || '').toLowerCase();
    const nextBackground = String(next.backgroundColor || '').toLowerCase();
    if (baseBackground && nextBackground && !colorsAreClose(baseBackground, nextBackground, 28)) {
        return false;
    }

    return true;
}

export function getWatermarkPreviewSvg(pageNumber, layout) {
    if (!state.watermark.enabled || !layout) {
        return '';
    }

    const centerX = layout.screenWidth / 2;
    const centerY = layout.screenHeight / 2;
    const opacity = clamp(Number(state.watermark.opacity || 0.2), 0.05, 0.9);
    const rotation = clamp(Number(state.watermark.rotation || 0), -90, 90);

    if (state.watermark.type === 'image') {
        if (!state.watermark.imageDataUrl) {
            return '';
        }

        const aspectRatio = Math.max(0.05, Number(state.watermark.imageAspectRatio || 1));
        const scale = clamp(Number(state.watermark.imageScale || 0.45), 0.15, 0.85);
        let width = layout.screenWidth * scale;
        let height = width / aspectRatio;
        const maxHeight = layout.screenHeight * 0.8;
        if (height > maxHeight) {
            height = maxHeight;
            width = height * aspectRatio;
        }

        const x = centerX - (width / 2);
        const y = centerY - (height / 2);

        return `
            <g opacity="${opacity}" transform="rotate(${rotation} ${centerX} ${centerY})">
                <image href="${escapeSvg(state.watermark.imageDataUrl)}" x="${x}" y="${y}" width="${width}" height="${height}" preserveAspectRatio="xMidYMid meet"></image>
            </g>
        `;
    }

    const text = String(state.watermark.text || '').trim();
    if (!text) {
        return '';
    }

    const fontSize = Math.max(18, Number(state.watermark.fontSize || 56));
    const color = /^#[0-9A-Fa-f]{6}$/.test(state.watermark.color || '')
        ? state.watermark.color
        : '#64748b';

    return `
        <g opacity="${opacity}">
            <text x="${centerX}" y="${centerY}" fill="${color}" font-size="${fontSize}" font-family="${fontFamilyToCss(state.watermark.fontFamily || 'Helvetica')}" font-weight="700" text-anchor="middle" dominant-baseline="middle" transform="rotate(${rotation} ${centerX} ${centerY})">${escapeSvg(text)}</text>
        </g>
    `;
}
